<script setup>
import { useRoute } from "vue-router";
import apiClient from "@/apiClient.js";
import { computed, onMounted, ref } from "vue";
import { useBreadcrumbStore } from "@/stores/BreadcrumbStore";
import AddAddress from "@/components/AddAddress";
import SearchContact from "@/components/SearchContact";
import TableList from "@/components/TableList.vue";
import FinanceBarChart from "@/components/FinanceBarChart.vue";
import ManualSaveControls from "@/components/ManualSaveControls.vue";
import TimeAgo from "@/components/TimeAgo.vue";
import dayjs from "dayjs";
import Router from "@/router/index.js";
import { useAuthStore } from "@/stores/AuthStore";
import {
  ArrowsRightLeftIcon,
  ArrowPathIcon,
  BuildingOffice2Icon,
  CalendarDaysIcon,
  ChartBarSquareIcon,
  CloudArrowDownIcon,
  ShieldCheckIcon,
  StarIcon,
  XMarkIcon,
} from "@heroicons/vue/24/solid";
import customerService from "@/services/CustomerService";
import useManualFormState from "@/composables/useManualFormState";

var advancedFormat = require("dayjs/plugin/advancedFormat");
dayjs.extend(advancedFormat);

const authStore = useAuthStore();
const route = useRoute();
const breadcrumbStore = useBreadcrumbStore();
const customerId = route.params.id;
const customer = ref({});
const companyForm = ref({});
const tickets = ref();
const ticketQuery = ref("");
const ticketLoading = ref(false);
const ticketSearchError = ref("");
const customerPurchaseOrders = ref([]);
const purchaseOrderPaying = ref({});
const {
  editable: companyEditing,
  saving: companySaving,
  isDirty: companyDirty,
  canSave: canSaveCompany,
  statusText: companyStatusText,
  statusTone: companyStatusTone,
  sync: syncCompanyForm,
  startEditing: startEditingCompany,
  cancelEditing: cancelEditingCompany,
  startSaving: startSavingCompany,
  markSaved: markCompanySaved,
  markSaveFailed: markCompanySaveFailed,
} = useManualFormState(companyForm, {
  leaveMessage: "You have unsaved company information changes. Leave this page?",
});
const invoices = computed(() => customer.value?.invoices ?? []);
const acceptedInvoices = computed(() =>
  invoices.value.filter((invoice) => invoice?.reference?.accepted_at || invoice?.paid_at),
);
const paidInvoices = computed(() =>
  invoices.value.filter((invoice) => invoice?.paid_at),
);
const rangeMonths = ref(12);
const rangeOptions = [
  { value: 12, label: "Last 12 months" },
  { value: 24, label: "Last 24 months" },
  { value: 36, label: "Last 3 years" },
  { value: 48, label: "Last 4 years" },
  { value: 60, label: "Last 5 years" },
];
const rangeLabel = computed(
  () => rangeOptions.find((option) => option.value === rangeMonths.value)?.label ?? "Range",
);
const hasInvoiceData = computed(() => invoices.value.length > 0);
const hasStoredMetrics = computed(() =>
  customer.value?.lifetime_value !== undefined
  || customer.value?.average_invoice_value !== undefined
  || customer.value?.avg_days_to_pay !== undefined
  || customer.value?.avg_jobs_per_year !== undefined
  || customer.value?.customer_age_days !== undefined,
);
const canViewFinancials = computed(() => authStore.hasFinanceRole());
const hasPurchaseOrders = computed(() => customerPurchaseOrders.value.length > 0);
const canViewInsights = computed(() =>
  authStore.hasAnyRole(["finance-department", "super"]),
);
const insightsOpen = ref(false);
const canRevealInsights = computed(
  () => canViewInsights.value && (hasInvoiceData.value || hasStoredMetrics.value),
);
const quoteViewerRoles = [
  "customer-service",
  "finance-department",
  "finance-audit",
  "finance-audits",
  "super",
];
const canViewQuotes = computed(() => authStore.hasAnyRole(quoteViewerRoles));
const canCreateQuote = computed(() => authStore.hasRole("customer-service"));
const canMergeCustomer = computed(() => authStore.hasRole("customer-service"));
const referencesExpanded = ref(false);
const REFERENCE_INITIAL_ROWS = 12;
const referenceFilter = ref("all");
const contactStatus = ref({ message: "", tone: "idle", savingId: null });
const addressStatus = ref({ message: "", tone: "idle", savingId: null });
const companyHealth = ref({ linked: false });
const companiesHouseNumber = ref("");
const companiesHouseSaving = ref(false);
const companiesHouseStatus = ref({ message: "", tone: "idle" });
const canManageCompaniesHouse = computed(() => authStore.hasRole("customer-service"));
const canViewCompanyRisk = computed(() => authStore.hasFinanceRole());
const companiesHouseCompany = computed(() => companyHealth.value?.company ?? null);
const companyRisk = computed(() => companyHealth.value?.risk ?? null);
const riskTone = computed(() => ({
  low: "bg-emerald-100 text-emerald-700 dark:bg-emerald-900/40 dark:text-emerald-300",
  moderate: "bg-amber-100 text-amber-700 dark:bg-amber-900/40 dark:text-amber-300",
  high: "bg-orange-100 text-orange-700 dark:bg-orange-900/40 dark:text-orange-300",
  critical: "bg-rose-100 text-rose-700 dark:bg-rose-900/40 dark:text-rose-300",
}[companyRisk.value?.band] ?? "bg-slate-100 text-slate-700 dark:bg-slate-700 dark:text-slate-200"));

const isPrimaryContact = (contact) =>
  Number(contact?.id) === Number(customer.value?.contact_id);
const isPrimaryAddress = (address) =>
  Number(address?.id) === Number(customer.value?.primary_address);

const sortedContacts = computed(() =>
  [...(customer.value?.contacts ?? [])].sort(
    (left, right) => Number(isPrimaryContact(right)) - Number(isPrimaryContact(left)),
  ),
);
const sortedAddresses = computed(() =>
  [...(customer.value?.address ?? [])].sort(
    (left, right) => Number(isPrimaryAddress(right)) - Number(isPrimaryAddress(left)),
  ),
);
const customerWebsiteUrl = computed(() => {
  const website = String(companyForm.value?.website ?? "").trim();
  if (!website) return "";
  return /^https?:\/\//i.test(website) ? website : `https://${website}`;
});

const apiErrorMessage = (error, fallback) => {
  const validationErrors = error?.response?.data?.errors;
  const firstValidationError = validationErrors
    ? Object.values(validationErrors).flat().find(Boolean)
    : null;
  return firstValidationError ?? error?.response?.data?.message ?? fallback;
};

const sumInvoices = (rows) =>
  rows.reduce(
    (sum, invoice) => sum + Number(invoice?.subtotal ?? 0) + Number(invoice?.vat ?? 0),
    0,
  );

const lifetimeValue = computed(() =>
  customer.value?.lifetime_value ?? sumInvoices(acceptedInvoices.value),
);
const outstandingValue = computed(() =>
  customer.value?.balance ?? sumInvoices(invoices.value.filter((invoice) => !invoice?.paid_at)),
);
const avgInvoiceValue = computed(() => {
  if (customer.value?.average_invoice_value !== undefined) {
    return customer.value.average_invoice_value ?? 0;
  }
  if (!acceptedInvoices.value.length) {
    return 0;
  }
  return Math.round(lifetimeValue.value / acceptedInvoices.value.length);
});
const avgDaysToPay = computed(() => {
  if (customer.value?.avg_days_to_pay !== undefined) {
    return customer.value.avg_days_to_pay ?? 0;
  }
  const paidInvoices = invoices.value.filter((invoice) => invoice?.paid_at);
  if (!paidInvoices.length) {
    return 0;
  }
  const days = paidInvoices
    .map((invoice) => {
      const paidAt = dayjs(invoice.paid_at);
      const sentAt = invoice.sent_at ? dayjs(invoice.sent_at) : null;
      const createdAt = invoice.created_at ? dayjs(invoice.created_at) : null;
      if (sentAt && !sentAt.isAfter(paidAt)) {
        return paidAt.diff(sentAt, "day");
      }
      if (createdAt && !createdAt.isAfter(paidAt)) {
        return paidAt.diff(createdAt, "day");
      }
      return null;
    })
    .filter((value) => value !== null);
  if (!days.length) {
    return 0;
  }
  const totalDays = days.reduce((sum, value) => sum + value, 0);
  return Math.round(totalDays / days.length);
});
const avgJobsPerYear = computed(() => {
  if (customer.value?.avg_jobs_per_year !== undefined) {
    return Math.round(Number(customer.value.avg_jobs_per_year ?? 0));
  }
  const jobs = customer.value?.jobs ?? [];
  if (!jobs.length) {
    return 0;
  }
  const invoiceItems = (invoices.value || [])
    .flatMap((invoice) => invoice?.items ?? [])
    .map((item) => item?.date)
    .filter(Boolean)
    .sort();
  const firstJobDate = invoiceItems[0];
  if (!firstJobDate) {
    return 0;
  }
  const yearsActive = Math.max(1, dayjs().diff(dayjs(firstJobDate), "day") / 365);
  return Math.round(jobs.length / yearsActive);
});
const customerAgeMonths = computed(() => {
  if (customer.value?.customer_age_days !== undefined) {
    return Math.round(Number(customer.value.customer_age_days ?? 0) / 30);
  }
  const invoiceItems = (invoices.value || [])
    .flatMap((invoice) => invoice?.items ?? [])
    .map((item) => item?.date)
    .filter(Boolean)
    .sort();
  const firstJobDate = invoiceItems[0];
  if (!firstJobDate) {
    return 0;
  }
  return dayjs().diff(dayjs(firstJobDate), "month");
});

const customerAgeLabel = computed(() => {
  const totalMonths = Math.max(1, customerAgeMonths.value);
  if (totalMonths >= 12) {
    const years = Math.floor(totalMonths / 12);
    return `${years} year${years === 1 ? "" : "s"}`;
  }
  return `${totalMonths} month${totalMonths === 1 ? "" : "s"}`;
});

const buildMonthlyTotals = (rows) => {
  const totals = new Map();
  rows.forEach((invoice) => {
    const date = getInvoiceRaisedDate(invoice);
    if (!date) {
      return;
    }
    const key = dayjs(date).format("YYYY-MM");
    totals.set(key, (totals.get(key) ?? 0) + Number(invoice?.subtotal ?? 0) + Number(invoice?.vat ?? 0));
  });
  return totals;
};

const getInvoiceRaisedDate = (invoice) => {
  const itemDates = (invoice?.items ?? [])
    .map((item) => item?.date)
    .filter(Boolean)
    .sort();
  return itemDates[0] ?? invoice?.created_at ?? null;
};

const acceptedInvoicesByMonth = computed(() => {
  const totals = new Map();
  acceptedInvoices.value.forEach((invoice) => {
    const date = getInvoiceRaisedDate(invoice);
    if (!date) {
      return;
    }
    const key = dayjs(date).format("YYYY-MM");
    if (!totals.has(key)) {
      totals.set(key, []);
    }
    totals.get(key).push(invoice);
  });
  return totals;
});

const paidInvoicesByMonth = computed(() => {
  const totals = new Map();
  paidInvoices.value.forEach((invoice) => {
    if (!invoice?.paid_at) {
      return;
    }
    const key = dayjs(invoice.paid_at).format("YYYY-MM");
    if (!totals.has(key)) {
      totals.set(key, []);
    }
    totals.get(key).push(invoice);
  });
  return totals;
});

const buildMonthlyBars = (totalsMap) => {
  const bars = [];
  const monthsBack = Math.max(1, rangeMonths.value);
  let cursor = dayjs().subtract(monthsBack - 1, "month").startOf("month");
  const end = dayjs().startOf("month");

  while (cursor.isBefore(end) || cursor.isSame(end)) {
    const key = cursor.format("YYYY-MM");
    const previousKey = cursor.subtract(1, "year").format("YYYY-MM");
    bars.push({
      label: cursor.format("MMM YY"),
      month: key,
      total: totalsMap.get(key) ?? 0,
      previous_total: totalsMap.get(previousKey) ?? 0,
    });
    cursor = cursor.add(1, "month");
  }

  return bars;
};

const customerMonthlyBars = computed(() => {
  const totals = buildMonthlyTotals(acceptedInvoices.value);
  return buildMonthlyBars(totals);
});

const incomeMonthlyBars = computed(() => {
  const totals = new Map();
  paidInvoices.value.forEach((invoice) => {
    if (!invoice?.paid_at) {
      return;
    }
    const key = dayjs(invoice.paid_at).format("YYYY-MM");
    totals.set(key, (totals.get(key) ?? 0) + Number(invoice?.subtotal ?? 0) + Number(invoice?.vat ?? 0));
  });
  return buildMonthlyBars(totals);
});

const invoiceTooltipRows = (tooltipItems) => {
  const item = tooltipItems?.[0];
  if (!item) {
    return [];
  }
  const monthKey = customerMonthlyBars.value[item.dataIndex]?.month;
  if (!monthKey) {
    return [];
  }
  const isPrevious = item.dataset?.label === "Prior year";
  const targetKey = isPrevious
    ? dayjs(`${monthKey}-01`).subtract(1, "year").format("YYYY-MM")
    : monthKey;
  const invoices = acceptedInvoicesByMonth.value.get(targetKey) || [];
  return invoices.map((invoice) => {
    const referenceName = invoice?.reference?.name ?? "—";
    const referenceTxt = invoice?.reference?.txt ?? "";
    const paidAt = invoice?.paid_at ? dayjs(invoice.paid_at).format("DD MMM YYYY") : "Unpaid";
    return `${referenceName} · ${referenceTxt} · ${paidAt}`;
  });
};

const incomeTooltipRows = (tooltipItems) => {
  const item = tooltipItems?.[0];
  if (!item) {
    return [];
  }
  const monthKey = incomeMonthlyBars.value[item.dataIndex]?.month;
  if (!monthKey) {
    return [];
  }
  const isPrevious = item.dataset?.label === "Prior year";
  const targetKey = isPrevious
    ? dayjs(`${monthKey}-01`).subtract(1, "year").format("YYYY-MM")
    : monthKey;
  const invoices = paidInvoicesByMonth.value.get(targetKey) || [];
  return invoices.map((invoice) => {
    const referenceName = invoice?.reference?.name ?? "—";
    const referenceTxt = invoice?.reference?.txt ?? "";
    const paidAt = invoice?.paid_at ? dayjs(invoice.paid_at).format("DD MMM YYYY") : "Unpaid";
    return `${referenceName} · ${referenceTxt} · ${paidAt}`;
  });
};

const formatCurrency = (value) =>
  new Intl.NumberFormat("en-GB", {
    style: "currency",
    currency: "GBP",
  }).format(Number(value ?? 0) / 100);

const asTimestamp = (value) => {
  if (!value) return 0;
  const parsed = dayjs(value);
  return parsed.isValid() ? parsed.valueOf() : 0;
};

const mergeReferenceRow = (map, key, payload) => {
  const existing = map.get(key) ?? {
    id: key,
    reference_txt: "",
    reference_name: "",
    payment_status_label: null,
    sort_at: null,
    job_id: null,
    quote_id: null,
    invoice_id: null,
  };

  if (!existing.reference_txt && payload.reference_txt) {
    existing.reference_txt = payload.reference_txt;
  }
  if (!existing.reference_name && payload.reference_name) {
    existing.reference_name = payload.reference_name;
  }

  if (payload.job_id) existing.job_id = payload.job_id;
  if (payload.quote_id) existing.quote_id = payload.quote_id;
  if (payload.invoice_id) existing.invoice_id = payload.invoice_id;
  if (!existing.payment_status_label && payload.payment_status_label) {
    existing.payment_status_label = payload.payment_status_label;
  }

  if (asTimestamp(payload.sort_at) > asTimestamp(existing.sort_at)) {
    existing.sort_at = payload.sort_at;
  }

  map.set(key, existing);
};

const unifiedReferences = computed(() => {
  const rows = new Map();

  (customer.value?.jobs ?? []).forEach((job) => {
    const referenceId = Number(job?.id ?? 0);
    const key = referenceId > 0 ? `ref-${referenceId}` : `job-${job?.id ?? "unknown"}`;
    mergeReferenceRow(rows, key, {
      reference_txt: job.txt ?? `REF-${job.id}`,
      reference_name: job.name ?? "Untitled Job",
      payment_status_label: job?.invoice?.payment_status_label ?? null,
      job_id: referenceId > 0 ? referenceId : null,
      sort_at: job.updated_at ?? job.created_at ?? null,
    });
  });

  if (canViewQuotes.value) {
    (customer.value?.quotes ?? []).forEach((quote) => {
      const referenceId = Number(quote?.reference?.id ?? 0);
      const key = referenceId > 0 ? `ref-${referenceId}` : `quote-${quote?.id ?? "unknown"}`;
      mergeReferenceRow(rows, key, {
        reference_txt: quote?.reference?.txt ?? `Quote #${quote.id}`,
        reference_name:
          quote?.reference?.name
          ?? quote?.display_name
          ?? quote?.description
          ?? `Quote #${quote.id}`,
        quote_id: Number(quote?.id ?? 0) || null,
        sort_at: quote?.updated_at ?? quote?.created_at ?? null,
      });
    });
  }

  if (canViewFinancials.value) {
    (customer.value?.invoices ?? []).forEach((invoice) => {
      const referenceId = Number(invoice?.reference?.id ?? 0);
      const key = referenceId > 0 ? `ref-${referenceId}` : `invoice-${invoice?.id ?? "unknown"}`;
      mergeReferenceRow(rows, key, {
        reference_txt: invoice?.reference?.txt ?? `INV-${invoice.id}`,
        reference_name: invoice?.reference?.name ?? "Invoice",
        payment_status_label: invoice?.payment_status_label ?? null,
        invoice_id: Number(invoice?.id ?? 0) || null,
        sort_at:
          invoice?.reference?.updated_at
          ?? invoice?.updated_at
          ?? invoice?.created_at
          ?? null,
      });
    });
  }

  return Array.from(rows.values())
    .map((row) => ({
      ...row,
      reference_txt:
        row.reference_txt
        || (row.job_id ? `REF-${row.job_id}` : row.quote_id ? `Quote #${row.quote_id}` : `INV-${row.invoice_id}`),
      reference_name:
        row.reference_name
        || (row.job_id ? "Untitled Job" : row.quote_id ? `Quote #${row.quote_id}` : "Invoice"),
    }))
    .sort((a, b) => {
    const aTs = a.sort_at ? dayjs(a.sort_at).valueOf() : 0;
    const bTs = b.sort_at ? dayjs(b.sort_at).valueOf() : 0;
    if (aTs !== bTs) {
      return bTs - aTs;
    }
    return String(b.reference_txt ?? "").localeCompare(String(a.reference_txt ?? ""));
  });
});

const filteredReferences = computed(() => {
  if (referenceFilter.value === "all") {
    return unifiedReferences.value;
  }

  return unifiedReferences.value.filter((row) => {
    if (referenceFilter.value === "jobs") return Boolean(row.job_id);
    if (referenceFilter.value === "quotes") return Boolean(row.quote_id);
    if (referenceFilter.value === "invoices") return Boolean(row.invoice_id);
    return true;
  });
});

const visibleReferences = computed(() =>
  referencesExpanded.value
    ? filteredReferences.value
    : filteredReferences.value.slice(0, REFERENCE_INITIAL_ROWS),
);

const hiddenReferenceCount = computed(() =>
  Math.max(filteredReferences.value.length - REFERENCE_INITIAL_ROWS, 0),
);

const toggleReferencesExpanded = () => {
  referencesExpanded.value = !referencesExpanded.value;
};

const setReferenceFilter = (nextFilter) => {
  referenceFilter.value = nextFilter;
  referencesExpanded.value = false;
};

const referenceFilterOptions = computed(() => {
  const rows = unifiedReferences.value;
  const jobs = rows.filter((row) => Boolean(row.job_id)).length;
  const quotes = rows.filter((row) => Boolean(row.quote_id)).length;
  const invoices = rows.filter((row) => Boolean(row.invoice_id)).length;

  return [
    { value: "all", label: `All (${rows.length})`, visible: true },
    { value: "jobs", label: `Jobs (${jobs})`, visible: jobs > 0 },
    { value: "quotes", label: `Quotes (${quotes})`, visible: canViewQuotes.value && quotes > 0 },
    { value: "invoices", label: `Invoices (${invoices})`, visible: canViewFinancials.value && invoices > 0 },
  ];
});

const openReferenceJob = (item) => {
  if (!item?.job_id) return;
  Router.push("/reference/" + item.job_id);
};

const openReferenceQuote = (item) => {
  if (!item?.quote_id) return;
  Router.push("/quotes/" + item.quote_id);
};

const openReferenceInvoice = (item) => {
  if (!item?.invoice_id) return;
  Router.push("/invoice/" + item.invoice_id);
};

const buildCompanyForm = (value = {}) => ({
  company: value.company ?? "",
  website: value.website ?? "",
  supplier_reference: value.supplier_reference ?? "",
  credit_days: value.credit_days ?? 0,
  auto_invoicing: String(value.auto_invoicing ?? "0"),
  invoice_reminders: String(value.invoice_reminders ?? "1"),
  notes: value.notes ?? "",
});

const applyCustomer = (nextCustomer) => {
  customer.value = nextCustomer ?? {};
  breadcrumbStore.setValue(
    customer.value.company ?? customer.value.contact?.name,
  );
  syncCompanyForm(buildCompanyForm(customer.value));
};

const loadCustomer = async () => {
  const response = await apiClient.get(`/api/customer/${customerId}`);
  applyCustomer(response.data);
};

const refreshCustomerRelationships = async () => {
  const response = await apiClient.get(`/api/customer/${customerId}`);
  const nextCustomer = response.data ?? {};
  customer.value = {
    ...customer.value,
    contact_id: nextCustomer.contact_id ?? null,
    contact: nextCustomer.contact ?? null,
    contacts: nextCustomer.contacts ?? [],
    primary_address: nextCustomer.primary_address ?? null,
    address: nextCustomer.address ?? [],
    created_at: nextCustomer.created_at ?? customer.value.created_at,
  };
};

const loadPurchaseOrders = async () => {
  if (!canViewFinancials.value) {
    customerPurchaseOrders.value = [];
    return;
  }

  const response = await apiClient.get(`/api/customer/${customerId}/purchase-orders`);
  customerPurchaseOrders.value = response.data ?? [];
};

const openPurchaseOrderInvoice = (purchaseOrder) => {
  Router.push(`/purchase-orders/${purchaseOrder.id}`);
};

const markPurchaseOrderPaid = async (purchaseOrder) => {
  if (purchaseOrderPaying.value[purchaseOrder.id]) {
    return;
  }

  purchaseOrderPaying.value = {
    ...purchaseOrderPaying.value,
    [purchaseOrder.id]: true,
  };

  try {
    await apiClient.put(`/api/purchase-orders/${purchaseOrder.id}/paid`);
    await loadCustomer();
    await loadPurchaseOrders();
  } finally {
    purchaseOrderPaying.value = {
      ...purchaseOrderPaying.value,
      [purchaseOrder.id]: false,
    };
  }
};

const loadTickets = async () => {
  ticketLoading.value = true;
  ticketSearchError.value = "";

  try {
    const query = ticketQuery.value.trim();
    const response = await apiClient.get(`/api/customer/${customerId}/tickets`, {
      params: query ? { query } : {},
    });
    tickets.value = response.data ?? [];
  } catch (error) {
    ticketSearchError.value =
      error?.response?.data?.message ?? "Unable to search tickets.";
  } finally {
    ticketLoading.value = false;
  }
};

const searchTickets = async () => {
  if (ticketQuery.value.trim().length < 2) {
    ticketSearchError.value = "Enter at least two characters.";
    return;
  }

  await loadTickets();
};

const clearTicketSearch = async () => {
  ticketQuery.value = "";
  await loadTickets();
};

const loadCompanyHealth = async () => {
  const response = await apiClient.get(`/api/customer/${customerId}/company-health`);
  companyHealth.value = response.data ?? { linked: false };
  if (companyHealth.value?.company?.company_number) {
    companiesHouseNumber.value = companyHealth.value.company.company_number;
  }
};

const linkCompaniesHouse = async () => {
  if (!companiesHouseNumber.value.trim() || companiesHouseSaving.value) return;
  companiesHouseSaving.value = true;
  companiesHouseStatus.value = { message: "Checking company number...", tone: "saving" };
  try {
    const response = await apiClient.put(`/api/customer/${customerId}/company-health`, {
      company_number: companiesHouseNumber.value,
    });
    companyHealth.value = response.data;
    companiesHouseNumber.value = response.data?.company?.company_number ?? companiesHouseNumber.value;
    companiesHouseStatus.value = {
      message: `Linked to ${response.data?.company?.company_name}. Initial monitoring has been queued.`,
      tone: "success",
    };
  } catch (error) {
    companiesHouseStatus.value = {
      message: apiErrorMessage(error, "Unable to link this Companies House company."),
      tone: "error",
    };
  } finally {
    companiesHouseSaving.value = false;
  }
};

const unlinkCompaniesHouse = async () => {
  if (!window.confirm("Remove this Companies House link from the customer?")) return;
  companiesHouseSaving.value = true;
  try {
    await apiClient.delete(`/api/customer/${customerId}/company-health`);
    companyHealth.value = { linked: false };
    companiesHouseNumber.value = "";
    companiesHouseStatus.value = { message: "Companies House link removed.", tone: "success" };
  } catch (error) {
    companiesHouseStatus.value = {
      message: apiErrorMessage(error, "Unable to remove the Companies House link."),
      tone: "error",
    };
  } finally {
    companiesHouseSaving.value = false;
  }
};

onMounted(async () => {
  await loadCustomer();
  await loadPurchaseOrders();
  await loadTickets();
  await loadCompanyHealth();
});

const saveCustomer = async () => {
  if (!companyDirty.value) {
    cancelEditingCompany();
    return;
  }

  startSavingCompany();

  try {
    await apiClient.post(`/api/customer/${customer.value.id}`, {
      _method: "PUT",
      ...companyForm.value,
    });
    await loadCustomer();
    markCompanySaved(buildCompanyForm(customer.value));
  } catch (error) {
    markCompanySaveFailed(
      apiErrorMessage(error, "Unable to save customer changes."),
    );
  }
};

const setContactStatus = (message, tone = "idle", savingId = null) => {
  contactStatus.value = { message, tone, savingId };
};

const setAddressStatus = (message, tone = "idle", savingId = null) => {
  addressStatus.value = { message, tone, savingId };
};

const addSelectedContact = async inContact => {
  if (customer.value.contacts.some(contact => contact.id === inContact.id))
    return

  setContactStatus("Adding contact...", "saving", `add-${inContact.id}`);
  try {
    await apiClient.post('/api/customer/' + customer.value.id + '/attach-contact', {
      contact_id: inContact.id,
    });
    await refreshCustomerRelationships();
    setContactStatus("Contact added.", "success");
  } catch (error) {
    setContactStatus(apiErrorMessage(error, "Unable to add contact."), "error");
  }
}

const deleteContact = async inContact => {
  if (isPrimaryContact(inContact)) {
    setContactStatus("Choose another primary contact before removing this one.", "error");
    return;
  }

  setContactStatus("Removing contact...", "saving", inContact.id);
  try {
    await apiClient.post('/api/customer/' + customer.value.id + '/detach-contact', {
      contact_id: inContact.id,
    });
    await refreshCustomerRelationships();
    setContactStatus("Contact removed.", "success");
  } catch (error) {
    setContactStatus(apiErrorMessage(error, "Unable to remove contact."), "error");
  }
}

const saveContact = async contact => {
  setContactStatus("Saving contact...", "saving", contact.id);
  try {
    await apiClient.post('/api/contact/' + contact.id, {
      _method: 'PUT',
      ...contact,
    });
    await refreshCustomerRelationships();
    setContactStatus("Contact saved.", "success");
  } catch (error) {
    setContactStatus(apiErrorMessage(error, "Unable to save contact."), "error");
  }
}

const setPrimaryContact = async contact => {
  if (isPrimaryContact(contact)) return;

  setContactStatus("Updating primary contact...", "saving", contact.id);
  try {
    await apiClient.post(`/api/customer/${customer.value.id}/primary-contact`, {
      contact_id: contact.id,
    });
    await refreshCustomerRelationships();
    setContactStatus(`${contact.name} is now the primary contact.`, "success");
  } catch (error) {
    setContactStatus(apiErrorMessage(error, "Unable to update the primary contact."), "error");
  }
};

const createdAddress = async inAddress => {
  setAddressStatus("Adding address...", "saving", `add-${inAddress.id}`);
  try {
    await apiClient.post('/api/customer/' + customer.value.id + '/attach-address', {
      address_id: inAddress.id,
    });
    await refreshCustomerRelationships();
    setAddressStatus("Address added.", "success");
  } catch (error) {
    setAddressStatus(apiErrorMessage(error, "Unable to add address."), "error");
  }
}

const deleteAddress = async inAddress => {
  if (isPrimaryAddress(inAddress)) {
    setAddressStatus("Choose another primary address before removing this one.", "error");
    return;
  }

  setAddressStatus("Removing address...", "saving", inAddress.id);
  try {
    await apiClient.post('/api/customer/' + customer.value.id + '/detach-address', {
      address_id: inAddress.id,
    });
    await refreshCustomerRelationships();
    setAddressStatus("Address removed.", "success");
  } catch (error) {
    setAddressStatus(apiErrorMessage(error, "Unable to remove address."), "error");
  }
}
const saveAddress = async address => {
  setAddressStatus("Saving address...", "saving", address.id);
  try {
    await apiClient.post('/api/address/' + address.id, {
      _method: 'PUT',
      ...address,
    });
    await refreshCustomerRelationships();
    setAddressStatus("Address saved.", "success");
  } catch (error) {
    setAddressStatus(apiErrorMessage(error, "Unable to save address."), "error");
  }
}

const setPrimaryAddress = async address => {
  if (isPrimaryAddress(address)) return;

  setAddressStatus("Updating primary address...", "saving", address.id);
  try {
    await apiClient.post(`/api/customer/${customer.value.id}/primary-address`, {
      address_id: address.id,
    });
    await refreshCustomerRelationships();
    setAddressStatus("Primary address updated.", "success");
  } catch (error) {
    setAddressStatus(apiErrorMessage(error, "Unable to update the primary address."), "error");
  }
};

const exportInvoices = () => {
  customerService.exportInvoices(customer.value.id)
}
</script>
<template>
  <div>
    <div class="flex justify-end gap-2 mb-4">
      <button
        v-if="canRevealInsights && !insightsOpen"
        class="btn accent px-3"
        type="button"
        @click.prevent="insightsOpen = true"
        aria-expanded="false"
        aria-controls="customer-insights-panel"
        title="Show customer insights"
      >
        <ChartBarSquareIcon class="h-5 w-5" />
      </button>
      <router-link
        v-if="canMergeCustomer && customer.id"
        :to="{ name: 'customer-merge', params: { id: customer.id } }"
        class="btn px-3"
        title="Merge customer"
      >
        <ArrowsRightLeftIcon class="h-5 w-5" />
      </router-link>
      <router-link to="/customer" class="btn">
        Back to Customers
      </router-link>
    </div>

    <form-box
      id="customer-insights-panel"
      header="Customer Insights"
      v-if="canRevealInsights && insightsOpen"
    >
      <template #nav>
        <button
          class="btn cancel px-3"
          type="button"
          @click.prevent="insightsOpen = false"
          title="Close customer insights"
        >
          <XMarkIcon class="h-5 w-5" />
        </button>
      </template>
      <div class="space-y-4">
        <div class="grid gap-4 md:grid-cols-6">
          <div class="rounded-2xl border border-slate-200 bg-white p-4 shadow-sm dark:border-slate-700 dark:bg-slate-800">
            <p class="text-xs uppercase tracking-wide text-slate-500">Lifetime value</p>
            <p class="mt-2 text-xl font-semibold text-emerald-600">
              {{ formatCurrency(lifetimeValue) }}
            </p>
            <p class="mt-1 text-xs text-slate-400">Accepted invoices</p>
          </div>
          <div class="rounded-2xl border border-slate-200 bg-white p-4 shadow-sm dark:border-slate-700 dark:bg-slate-800">
            <p class="text-xs uppercase tracking-wide text-slate-500">Outstanding</p>
            <p class="mt-2 text-xl font-semibold text-rose-500">
              {{ formatCurrency(outstandingValue) }}
            </p>
            <p class="mt-1 text-xs text-slate-400">Unpaid balance</p>
          </div>
          <div class="rounded-2xl border border-slate-200 bg-white p-4 shadow-sm dark:border-slate-700 dark:bg-slate-800">
            <p class="text-xs uppercase tracking-wide text-slate-500">Average invoice</p>
            <p class="mt-2 text-xl font-semibold text-slate-900 dark:text-slate-100">
              {{ formatCurrency(avgInvoiceValue) }}
            </p>
            <p class="mt-1 text-xs text-slate-400">Accepted only</p>
          </div>
          <div class="rounded-2xl border border-slate-200 bg-white p-4 shadow-sm dark:border-slate-700 dark:bg-slate-800">
            <p class="text-xs uppercase tracking-wide text-slate-500">Avg time to pay</p>
            <p class="mt-2 text-xl font-semibold text-slate-900 dark:text-slate-100">
              {{ avgDaysToPay }} days
            </p>
            <p class="mt-1 text-xs text-slate-400">Sent to paid</p>
          </div>
          <div class="rounded-2xl border border-slate-200 bg-white p-4 shadow-sm dark:border-slate-700 dark:bg-slate-800">
            <p class="text-xs uppercase tracking-wide text-slate-500">Avg jobs / year</p>
            <p class="mt-2 text-xl font-semibold text-slate-900 dark:text-slate-100">
              {{ avgJobsPerYear }}
            </p>
            <p class="mt-1 text-xs text-slate-400">Since first job</p>
          </div>
          <div class="rounded-2xl border border-slate-200 bg-white p-4 shadow-sm dark:border-slate-700 dark:bg-slate-800">
            <p class="text-xs uppercase tracking-wide text-slate-500">Customer age</p>
            <p class="mt-2 text-xl font-semibold text-slate-900 dark:text-slate-100">
              {{ customerAgeLabel }}
            </p>
            <p class="mt-1 text-xs text-slate-400">Since first invoice item</p>
          </div>
        </div>

        <div class="rounded-2xl border border-slate-200 bg-white p-5 shadow-sm dark:border-slate-700 dark:bg-slate-800">
          <div class="flex items-center justify-between">
            <div>
              <p class="text-xs uppercase tracking-wide text-slate-500">Accepted invoice value</p>
              <h3 class="text-lg font-semibold text-slate-900 dark:text-slate-100">
                Last 12 months vs prior year
              </h3>
            </div>
            <div class="flex items-center gap-3 text-xs text-slate-400">
              <span>{{ rangeLabel }}</span>
              <select v-model.number="rangeMonths" class="input text-xs rounded-md">
                <option v-for="option in rangeOptions" :key="option.value" :value="option.value">
                  {{ option.label }}
                </option>
              </select>
            </div>
          </div>
          <div class="mt-4">
            <FinanceBarChart
              :bars="customerMonthlyBars"
              :showLegend="true"
              current-label="Last 12 months"
              previous-label="Prior year"
              :tooltip-rows="invoiceTooltipRows"
            />
          </div>
        </div>

        <div class="rounded-2xl border border-slate-200 bg-white p-5 shadow-sm dark:border-slate-700 dark:bg-slate-800">
          <div class="flex items-center justify-between">
            <div>
              <p class="text-xs uppercase tracking-wide text-slate-500">Income received</p>
              <h3 class="text-lg font-semibold text-slate-900 dark:text-slate-100">
                Payments by month
              </h3>
            </div>
            <span class="text-xs text-slate-400">{{ rangeLabel }}</span>
          </div>
          <div class="mt-4">
            <FinanceBarChart
              :bars="incomeMonthlyBars"
              :showLegend="true"
              current-label="Last 12 months"
              previous-label="Prior year"
              :tooltip-rows="incomeTooltipRows"
            />
          </div>
        </div>
      </div>
    </form-box>

    <form-box>
      <template #header>
        <div>
          <div>Company Details</div>
          <div
            v-if="customer.created_at"
            class="mt-1.5 flex flex-wrap items-center gap-x-1.5 gap-y-1 text-xs font-normal text-slate-500 dark:text-slate-400"
          >
            <CalendarDaysIcon class="h-4 w-4 text-slate-400" aria-hidden="true" />
            <span>Created</span>
            <TimeAgo :data="customer.created_at" />
          </div>
        </div>
      </template>
      <template v-slot:nav>
        <ManualSaveControls
          :editing="companyEditing"
          :saving="companySaving"
          :canSave="canSaveCompany"
          :statusText="companyStatusText"
          :statusTone="companyStatusTone"
          @edit="startEditingCompany"
          @cancel="cancelEditingCompany"
          @save="saveCustomer"
        />
      </template>
      <form class="grid grid-cols-auto md:grid-cols-2 gap-4">
        <label>Company</label>
        <input type="text" v-model="companyForm.company" :disabled="!companyEditing" />
        <label>Website</label>
        <input v-if="companyEditing" type="text" v-model="companyForm.website" />
        <a
          v-else-if="companyForm.website"
          :href="customerWebsiteUrl"
          target="_blank"
          rel="noopener noreferrer"
          class="self-center text-cyan-700 hover:underline dark:text-cyan-300"
        >
          {{ companyForm.website }}
        </a>
        <span v-else class="self-center text-slate-400">Not provided</span>
        <label>Notes</label>
        <textarea v-model="companyForm.notes" :disabled="!companyEditing"></textarea>
      </form>
    </form-box>

    <form-box header="Companies House">
      <template #nav>
        <router-link
          v-if="companyHealth.linked && canViewCompanyRisk"
          :to="{ name: 'customer-company-health', params: { id: customerId } }"
          class="btn accent"
        >
          View company health
        </router-link>
      </template>

      <div
        v-if="companiesHouseStatus.message"
        class="mb-4 rounded-xl border px-4 py-3 text-sm"
        :class="companiesHouseStatus.tone === 'error'
          ? 'border-rose-200 bg-rose-50 text-rose-700 dark:border-rose-800 dark:bg-rose-950/30 dark:text-rose-300'
          : companiesHouseStatus.tone === 'success'
            ? 'border-emerald-200 bg-emerald-50 text-emerald-700 dark:border-emerald-800 dark:bg-emerald-950/30 dark:text-emerald-300'
            : 'border-slate-200 bg-slate-50 text-slate-600 dark:border-slate-700 dark:bg-slate-800 dark:text-slate-300'"
        aria-live="polite"
      >
        {{ companiesHouseStatus.message }}
      </div>

      <div v-if="companyHealth.linked" class="grid gap-4 lg:grid-cols-[minmax(0,1fr)_auto]">
        <div class="flex min-w-0 items-start gap-3">
          <span class="rounded-xl bg-cyan-100 p-2.5 text-cyan-700 dark:bg-cyan-900/40 dark:text-cyan-300">
            <BuildingOffice2Icon class="h-6 w-6" />
          </span>
          <div class="min-w-0">
            <a
              :href="companiesHouseCompany.companies_house_url"
              target="_blank"
              rel="noopener noreferrer"
              class="font-semibold text-cyan-700 hover:underline dark:text-cyan-300"
            >
              {{ companiesHouseCompany.company_name }}
            </a>
            <div class="mt-1 flex flex-wrap items-center gap-2 text-sm text-slate-500 dark:text-slate-400">
              <span>{{ companiesHouseCompany.company_number }}</span>
              <span class="rounded-full bg-slate-100 px-2 py-0.5 capitalize dark:bg-slate-700">
                {{ companiesHouseCompany.company_status || "Status pending" }}
              </span>
              <span v-if="companiesHouseCompany.last_successful_at">
                Checked <TimeAgo :data="companiesHouseCompany.last_successful_at" />
              </span>
              <span v-else>Initial check queued</span>
            </div>
            <p v-if="companiesHouseCompany.last_error" class="mt-2 text-sm text-rose-600 dark:text-rose-300">
              {{ companiesHouseCompany.last_error }}
            </p>
            <div class="mt-3 flex flex-wrap gap-2 text-xs">
              <span
                v-if="companiesHouseCompany.accounts?.next_accounts?.overdue"
                class="rounded-full bg-rose-100 px-2.5 py-1 font-semibold text-rose-700 dark:bg-rose-900/40 dark:text-rose-300"
              >Accounts overdue</span>
              <span
                v-if="companiesHouseCompany.confirmation_statement?.overdue"
                class="rounded-full bg-amber-100 px-2.5 py-1 font-semibold text-amber-700 dark:bg-amber-900/40 dark:text-amber-300"
              >Confirmation statement overdue</span>
              <span
                v-if="!companiesHouseCompany.accounts?.next_accounts?.overdue && !companiesHouseCompany.confirmation_statement?.overdue"
                class="inline-flex items-center gap-1 rounded-full bg-emerald-100 px-2.5 py-1 font-semibold text-emerald-700 dark:bg-emerald-900/40 dark:text-emerald-300"
              ><ShieldCheckIcon class="h-3.5 w-3.5" /> No overdue statutory filings</span>
            </div>
          </div>
        </div>

        <div class="flex flex-col items-end gap-3">
          <div
            v-if="canViewCompanyRisk && companyRisk?.score !== null && companyRisk?.score !== undefined"
            class="min-w-[160px] rounded-2xl px-4 py-3 text-right"
            :class="riskTone"
          >
            <p class="text-xs font-semibold uppercase tracking-wide">Company risk</p>
            <p class="mt-1 text-2xl font-bold">{{ companyRisk.score }}<span class="text-sm font-normal"> / 100</span></p>
            <p class="text-xs capitalize">{{ companyRisk.band }} · {{ companyRisk.confidence }} confidence</p>
          </div>
          <button
            v-if="canManageCompaniesHouse"
            type="button"
            class="text-xs text-rose-600 hover:underline dark:text-rose-300"
            :disabled="companiesHouseSaving"
            @click="unlinkCompaniesHouse"
          >
            Remove company link
          </button>
        </div>
      </div>

      <div v-else-if="canManageCompaniesHouse" class="max-w-2xl">
        <p class="mb-3 text-sm text-slate-500 dark:text-slate-400">
          Enter the registered company number. Neptune will verify the legal entity before monitoring it every seven days.
        </p>
        <div class="flex flex-col gap-2 sm:flex-row">
          <input
            v-model="companiesHouseNumber"
            class="input flex-1 uppercase"
            type="text"
            maxlength="20"
            autocomplete="off"
            placeholder="For example 12345678 or SC123456"
            @keyup.enter="linkCompaniesHouse"
          />
          <button
            type="button"
            class="btn accent inline-flex items-center justify-center gap-2"
            :disabled="companiesHouseSaving || !companiesHouseNumber.trim()"
            @click="linkCompaniesHouse"
          >
            <ArrowPathIcon v-if="companiesHouseSaving" class="h-4 w-4 animate-spin" />
            {{ companiesHouseSaving ? "Checking..." : "Verify and link" }}
          </button>
        </div>
      </div>
      <p v-else class="text-sm text-slate-500 dark:text-slate-400">
        No Companies House company is linked. A customer-service user can add the company number.
      </p>
    </form-box>

    <form-box header="Billing Settings">
      <template v-slot:nav>
        <ManualSaveControls
          :editing="companyEditing"
          :saving="companySaving"
          :canSave="canSaveCompany"
          :statusText="companyStatusText"
          :statusTone="companyStatusTone"
          @edit="startEditingCompany"
          @cancel="cancelEditingCompany"
          @save="saveCustomer"
        />
      </template>
      <form class="grid grid-cols-auto md:grid-cols-2 gap-4">
        <label>Supplier Reference</label>
        <input type="text" v-model="companyForm.supplier_reference" :disabled="!companyEditing" />
        <label>Credit (Days)</label>
        <input type="number" v-model="companyForm.credit_days" :disabled="!companyEditing" min="0" max="60" />
        <label>Auto Invoicing</label>
        <select v-model="companyForm.auto_invoicing" :disabled="!companyEditing">
          <option value="1">Enabled</option>
          <option value="0">Disabled</option>
        </select>
        <label>Invoice Reminders</label>
        <select v-model="companyForm.invoice_reminders" :disabled="!companyEditing">
          <option value="1">Enabled</option>
          <option value="0">Disabled</option>
        </select>
      </form>
    </form-box>

    <form-box header="Contacts">
      <div
        v-if="contactStatus.message"
        class="mb-3 text-sm"
        :class="contactStatus.tone === 'error' ? 'text-rose-600' : contactStatus.tone === 'success' ? 'text-emerald-600' : 'text-slate-500'"
        aria-live="polite"
      >
        {{ contactStatus.message }}
      </div>
      <TableList :modelValue="sortedContacts" :cols="[
        { name: 'Name', row: 'name', editable: true },
        { name: 'Email', row: 'email', editable: true },
        { name: 'Phone', row: 'phone', editable: true },
      ]" :editable="true" :deletable="true" :canDelete="contact => !isPrimaryContact(contact)" :box="false"
        @delete="deleteContact" @save="saveContact">
        <template #actions="{ line }">
          <span
            v-if="isPrimaryContact(line)"
            class="mr-2 inline-flex items-center gap-1 rounded-full bg-emerald-100 px-2.5 py-1 text-xs font-semibold text-emerald-700 dark:bg-emerald-900/40 dark:text-emerald-300"
          >
            <StarIcon class="h-3.5 w-3.5" />
            Primary
          </span>
          <button
            v-else
            type="button"
            class="btn mr-2 text-xs"
            :disabled="contactStatus.tone === 'saving'"
            @click.stop.prevent="setPrimaryContact(line)"
          >
            <StarIcon class="icon" />
            <span class="hidden lg:inline">Make primary</span>
          </button>
        </template>
      </TableList>
      <p v-if="sortedContacts.length && !customer.contact_id" class="mt-2 text-sm text-amber-600">
        No primary contact selected.
      </p>
      <search-contact class="mt-4" @selected="addSelectedContact" />
    </form-box>
    <form-box header="Addresses">
      <div
        v-if="addressStatus.message"
        class="mb-3 text-sm"
        :class="addressStatus.tone === 'error' ? 'text-rose-600' : addressStatus.tone === 'success' ? 'text-emerald-600' : 'text-slate-500'"
        aria-live="polite"
      >
        {{ addressStatus.message }}
      </div>
      <TableList :modelValue="sortedAddresses" :cols="[{ name: 'Address', row: 'data', editable: true, type: 'textarea' }]"
        :editable="true" :deletable="true" :canDelete="address => !isPrimaryAddress(address)" :box="false"
        @delete="deleteAddress" @save="saveAddress">
        <template #actions="{ line }">
          <span
            v-if="isPrimaryAddress(line)"
            class="mr-2 inline-flex items-center gap-1 rounded-full bg-emerald-100 px-2.5 py-1 text-xs font-semibold text-emerald-700 dark:bg-emerald-900/40 dark:text-emerald-300"
          >
            <StarIcon class="h-3.5 w-3.5" />
            Primary
          </span>
          <button
            v-else
            type="button"
            class="btn mr-2 text-xs"
            :disabled="addressStatus.tone === 'saving'"
            @click.stop.prevent="setPrimaryAddress(line)"
          >
            <StarIcon class="icon" />
            <span class="hidden lg:inline">Make primary</span>
          </button>
        </template>
      </TableList>
      <p v-if="sortedAddresses.length && !customer.primary_address" class="mt-2 text-sm text-amber-600">
        No primary address selected.
      </p>
      <AddAddress @created="createdAddress" />
    </form-box>

    <form-box v-if="canViewFinancials" header="Purchase Orders">
      <div v-if="hasPurchaseOrders" class="overflow-hidden rounded-2xl">
        <table class="w-full table-auto">
          <thead class="bg-slate-50 dark:bg-slate-900/40">
            <tr class="text-left text-xs uppercase tracking-wide text-slate-500 dark:text-slate-300">
              <th class="px-4 py-3">PO</th>
              <th class="px-4 py-3">Status</th>
              <th class="px-4 py-3 text-right">Invoices</th>
              <th class="px-4 py-3 text-right">Total</th>
              <th class="px-4 py-3 text-right">Unpaid</th>
              <th class="px-4 py-3 text-right">Actions</th>
            </tr>
          </thead>
          <tbody class="divide-y divide-slate-100 dark:divide-slate-800">
            <tr v-for="purchaseOrder in customerPurchaseOrders" :key="purchaseOrder.id">
              <td class="px-4 py-3">
                <div class="font-semibold text-slate-900 dark:text-slate-100">
                  {{ purchaseOrder.po_number }}
                </div>
                <div v-if="purchaseOrder.description" class="mt-1 text-xs text-slate-500">
                  {{ purchaseOrder.description }}
                </div>
              </td>
              <td class="px-4 py-3 text-sm capitalize text-slate-700 dark:text-slate-200">
                {{ purchaseOrder.status }}
              </td>
              <td class="px-4 py-3 text-right text-sm text-slate-700 dark:text-slate-200">
                {{ purchaseOrder.invoice_count ?? 0 }}
              </td>
              <td class="px-4 py-3 text-right font-semibold text-slate-900 dark:text-slate-100">
                {{ formatCurrency(purchaseOrder.total ?? 0) }}
              </td>
              <td class="px-4 py-3 text-right font-semibold text-rose-600">
                {{ formatCurrency(purchaseOrder.unpaid_total ?? 0) }}
              </td>
              <td class="px-4 py-3">
                <div class="flex flex-wrap justify-end gap-2">
                  <button
                    class="rounded-lg border border-slate-300 px-3 py-1.5 text-xs font-semibold text-slate-700 transition hover:bg-slate-100 dark:border-slate-600 dark:text-slate-200 dark:hover:bg-slate-800"
                    type="button"
                    @click.prevent="openPurchaseOrderInvoice(purchaseOrder)"
                  >
                    View
                  </button>
                  <button
                    class="rounded-lg border border-emerald-300 px-3 py-1.5 text-xs font-semibold text-emerald-700 transition hover:bg-emerald-50 disabled:opacity-50 dark:border-emerald-700 dark:text-emerald-300 dark:hover:bg-emerald-900/30"
                    type="button"
                    :disabled="purchaseOrder.payment_status_label === 'Paid' || purchaseOrderPaying[purchaseOrder.id]"
                    @click.prevent="markPurchaseOrderPaid(purchaseOrder)"
                  >
                    {{ purchaseOrder.payment_status_label === "Paid" ? "Paid" : "Mark paid" }}
                  </button>
                </div>
              </td>
            </tr>
          </tbody>
        </table>
      </div>
      <div v-else class="rounded-lg border border-dashed border-slate-300 px-4 py-3 text-sm text-slate-500 dark:border-slate-700 dark:text-slate-400">
        No purchase orders recorded for this customer.
      </div>
    </form-box>

    <form-box header="References" :collapsible="true">
      <template v-slot:nav>
        <button v-if="canViewFinancials" class="icon clickable" @click="exportInvoices">
          <CloudArrowDownIcon class="icon" />
        </button>
      </template>

      <div class="mb-3 flex flex-wrap items-center gap-2">
        <button
          v-for="option in referenceFilterOptions.filter((option) => option.visible)"
          :key="option.value"
          class="rounded-full border px-3 py-1 text-xs font-semibold transition"
          :class="referenceFilter === option.value
            ? 'border-slate-800 bg-slate-800 text-white dark:border-slate-200 dark:bg-slate-200 dark:text-slate-900'
            : 'border-slate-300 text-slate-600 hover:bg-slate-100 dark:border-slate-600 dark:text-slate-300 dark:hover:bg-slate-800'"
          @click.prevent="setReferenceFilter(option.value)"
        >
          {{ option.label }}
        </button>
      </div>

      <div v-if="visibleReferences.length" class="overflow-hidden rounded-2xl">
        <table class="w-full table-auto">
          <thead class="bg-slate-50 dark:bg-slate-900/40">
            <tr class="text-left text-xs uppercase tracking-wide text-slate-500 dark:text-slate-300">
              <th class="px-4 py-3">Reference</th>
              <th class="px-4 py-3">Name</th>
              <th class="px-4 py-3 text-right">Action</th>
            </tr>
          </thead>
          <tbody class="divide-y divide-slate-100 dark:divide-slate-800">
            <tr v-for="(item, index) in visibleReferences" :key="item.id" :class="[
              index % 2 === 0 ? 'bg-white dark:bg-slate-900/10' : 'bg-slate-50/80 dark:bg-slate-900/25',
              'hover:bg-slate-100 dark:hover:bg-slate-800/35',
            ]">
              <td class="px-4 py-3 text-sm font-semibold text-slate-900 dark:text-slate-100">
                {{ item.reference_txt }}
              </td>
              <td class="px-4 py-3 text-sm text-slate-700 dark:text-slate-200">
                {{ item.reference_name }}
              </td>
              <td class="px-4 py-3 text-right">
                <div class="flex flex-wrap justify-end gap-2">
                  <span v-if="canViewFinancials && item.payment_status_label"
                    class="inline-flex rounded-full border border-slate-200 bg-slate-100 px-2.5 py-1 text-xs font-semibold text-slate-700 dark:border-slate-700 dark:bg-slate-800 dark:text-slate-200">
                    {{ item.payment_status_label }}
                  </span>
                  <button v-if="item.job_id"
                    class="rounded-lg border border-slate-300 px-3 py-1.5 text-xs font-semibold text-slate-700 transition hover:bg-slate-100 dark:border-slate-600 dark:text-slate-200 dark:hover:bg-slate-800"
                    @click.prevent="openReferenceJob(item)">
                    Job
                  </button>
                  <button v-if="item.quote_id && canViewQuotes"
                    class="rounded-lg border border-slate-300 px-3 py-1.5 text-xs font-semibold text-slate-700 transition hover:bg-slate-100 dark:border-slate-600 dark:text-slate-200 dark:hover:bg-slate-800"
                    @click.prevent="openReferenceQuote(item)">
                    Quote
                  </button>
                  <button v-if="item.invoice_id && canViewFinancials"
                    class="rounded-lg border border-slate-300 px-3 py-1.5 text-xs font-semibold text-slate-700 transition hover:bg-slate-100 dark:border-slate-600 dark:text-slate-200 dark:hover:bg-slate-800"
                    @click.prevent="openReferenceInvoice(item)">
                    Invoice
                  </button>
                </div>
              </td>
            </tr>
          </tbody>
        </table>
      </div>
      <div v-else class="rounded-lg border border-dashed border-slate-300 px-4 py-3 text-sm text-slate-500 dark:border-slate-700 dark:text-slate-400">
        No references available for this customer.
      </div>

      <div v-if="hiddenReferenceCount > 0" class="mt-3">
        <button class="text-xs font-semibold text-slate-600 hover:text-slate-900 dark:text-slate-300 dark:hover:text-white"
          @click.prevent="toggleReferencesExpanded">
          {{ referencesExpanded ? "Show fewer references" : `Show ${hiddenReferenceCount} more references` }}
        </button>
      </div>

      <div class="flex flex-wrap gap-2 my-2">
        <router-link :to="{ name: 'reference-create', query: { customerId: customer.id } }"
          class="btn plus inline-block">
          + Job
        </router-link>
        <router-link v-if="canCreateQuote" :to="{ name: 'quotes-create', query: { customer_id: customer.id } }"
          class="btn plus inline-block">
          + Quote
        </router-link>
        <router-link v-if="canViewFinancials" :to="{ name: 'invoice-create', query: { customerId: customer.id } }"
          class="btn plus inline-block">
          + Invoice
        </router-link>
      </div>
    </form-box>

    <form-box header="Tickets" :collapsible="true" v-if="tickets">
      <form class="mb-3 flex flex-col gap-2 sm:flex-row" @submit.prevent="searchTickets">
        <input
          v-model="ticketQuery"
          type="search"
          class="input min-w-0 flex-1"
          placeholder="Search this customer's tickets"
          aria-label="Search this customer's tickets"
        />
        <div class="flex gap-2">
          <button type="submit" class="btn accent" :disabled="ticketLoading">
            {{ ticketLoading ? "Searching..." : "Search" }}
          </button>
          <button
            v-if="ticketQuery"
            type="button"
            class="btn"
            :disabled="ticketLoading"
            @click="clearTicketSearch"
          >
            Clear
          </button>
        </div>
      </form>
      <p v-if="ticketSearchError" class="mb-3 text-sm text-red-600 dark:text-red-400">
        {{ ticketSearchError }}
      </p>
      <TableList v-if="tickets.length" :modelValue="tickets" :cols="[
        { run: val => val.items[0]?.contact?.name, name: 'Contact' },
        { run: val => val.subject, name: 'Subject' },
        {
          run: val => dayjs(val.updated_at).format('Do MMM YY'),
          name: 'Updated',
        },
      ]" :box="false" @view="val => Router.push('/tickets/' + val.id)" linkTo="/tickets/" />
      <p v-else-if="!ticketLoading" class="mb-3 text-sm text-slate-500 dark:text-slate-400">
        {{
          ticketQuery
            ? "No tickets matched this search."
            : "No tickets are linked to this customer."
        }}
      </p>
      <router-link to="/tickets/create" class="btn plus my-2 inline-block">+ Ticket</router-link>
    </form-box>
  </div>
</template>
